import {
  ChangeEvent,
  Dispatch,
  useEffect,
  useMemo,
  useRef,
  useState,
  SetStateAction,
} from "react";
import { CommandPaletteRendererInterface } from "../../../../packages/excalidraw/element/types";
import { notionSearch } from "../../../services/notion";
import {
  getNotionPagesSnapshot,
  mapNotionPageResult,
  NotionPageListItem,
  prefetchNotionPages,
  subscribeNotionPagesCache,
} from "../../../data/notionPagesCache";
import {
  NotionIconElementType,
  processNotionIcon,
} from "../../../helper/canvasUtils";
import { MouseEventType } from "../../../types";
import {
  ArrowUturnLeftIcon,
  ArrowsUpDownIcon,
} from "@heroicons/react/24/outline";
import { handleSupabaseError } from "../../../services/utils";
import { classNames } from "../../../helper/classNames";
import { t } from "../../../../packages/excalidraw/i18n";
import {
  generateMindmapFromPage,
  generateMindmapFromDatabase,
  addGenerationPlaceholder,
  removeGenerationPlaceholder,
  refreshMapsForPage,
  EmptyMindmapError,
  EmptyDatabaseMindmapError,
} from "../../../data/mindmapGenerate";
import { getMindmapApi } from "../../../data/mindmapApiRef";
import {
  addStandaloneNotionCard,
  standaloneNotionCardSpec,
} from "../../../data/mindmapStandaloneCard";
import { existingMapIdsForPage } from "../../../data/mindmapRefresh";
import {
  filterNotionPages,
  mergeNotionPages,
} from "../../../helper/notionSearchMerge";

/**
 * The FALLBACK search is debounced. Typing normally costs no request at all —
 * the workspace list is already in memory and filtered there — so this only
 * runs for a workspace too large to have been listed exhaustively. Without the
 * debounce a six-letter query cost four round trips whose responses could land
 * out of order; a request id makes only the latest one apply.
 */
const SEARCH_DEBOUNCE_MS = 300;

type NotionPage = NotionPageListItem;

interface Props extends CommandPaletteRendererInterface {
  mouseClickEvent: MouseEventType;
  eventType?: string;
  closeModal: () => void;
  setSelectedPage?: Dispatch<SetStateAction<NotionPage>>;
  selectedPage?: NotionPage;
  renderPage?: boolean;
  element?: any;
}

const Search: React.FC<Props> = ({
  render,
  mouseClickEvent,
  closeModal,
  eventType,
  setSelectedPage,
  selectedPage,
  renderPage = false,
  element,
  excalidrawAppState,
}) => {
  const [query, setQuery] = useState("");
  const [error, setError] = useState<any>();
  const [workspace, setWorkspace] = useState(getNotionPagesSnapshot);
  const [remoteSearchResults, setRemoteSearchResults] = useState<NotionPage[]>(
    [],
  );
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchElementIndex, setSearchElementIndex] = useState(0);
  const [searchIndexVisible, setSearchIndexVisible] = useState(true);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  // When a picked page already has a map on the canvas, we replace its Generate
  // button with a Refresh-or-Copy choice keyed by the page id, remembering EVERY
  // existing map of that page — a page can be mapped more than once, and
  // refreshing only the first one silently left the others stale (C8#10).
  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    pageId: string;
    mapIds: string[];
  } | null>(null);
  // A generate/refresh is claimed synchronously here, not through `generatingId`:
  // React state only lands on the next render, so a second row click inside the
  // fetch window still saw the old value and built a second complete map with
  // its own undo entry (E8).
  const generateInFlight = useRef(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeq = useRef(0);
  const queryRef = useRef("");

  useEffect(() => {
    const syncFromCache = () => {
      const next = getNotionPagesSnapshot();
      setWorkspace(next);
      // The rows under the selection just changed; keeping the old index would
      // point it at a different page than the one that was highlighted.
      setSearchElementIndex(0);
      // A sign-out clears the cache but not this component's own copy of the
      // last fallback response — with a query still typed, the signed-out
      // account's rows survived the invalidation by being merged back in.
      if (next.status === "idle") {
        setRemoteSearchResults([]);
      }
    };
    syncFromCache();
    const unsubscribe = subscribeNotionPagesCache(syncFromCache);
    // A no-op once the list is loaded (sign-in usually got there first), and a
    // retry after a failed load — so re-opening the picker is the recovery.
    void prefetchNotionPages();
    return unsubscribe;
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
      searchSeq.current += 1;
    };
  }, []);

  const queryTrimmed = query.trim();
  const isQueryActive = queryTrimmed.length > 0;
  // Typing filters what is already here. Notion's own search is asked ONLY when
  // the in-memory list cannot be trusted to hold the answer: still loading,
  // failed, or truncated at the fetch cap.
  const listIsComplete = workspace.status === "loaded" && !workspace.partial;
  // Memoised because the list is now up to 500 rows and every hover flips
  // `searchIndexVisible`: without this, moving the pointer down the list re-ran
  // the whole filter+merge on each row.
  const displayedPages = useMemo(
    () =>
      isQueryActive
        ? mergeNotionPages(
            filterNotionPages(workspace.pages, queryTrimmed),
            remoteSearchResults,
          )
        : workspace.pages,
    [isQueryActive, workspace.pages, queryTrimmed, remoteSearchResults],
  );
  const isLoading = workspace.status === "loading" && !workspace.pages.length;
  // "idle" is the state a sign-out leaves behind. It matched no render branch,
  // so a picker open at that moment went permanently blank — no list, no
  // spinner, no retry.
  const loadFailed =
    workspace.status === "failed" || workspace.status === "idle";
  // The integration can be connected and shared with nothing at all. Without
  // this the picker falls through every branch and renders the blank box this
  // whole change exists to remove.
  const workspaceIsEmpty =
    workspace.status === "loaded" && workspace.pages.length === 0;
  const showRemoteSearchSpinner =
    isQueryActive && isSearchLoading && displayedPages.length === 0;

  // "Generate mindmap" belongs on page and database results in the import flow —
  // not when picking a page to link to an element.
  const canGenerateMindmap = (page: NotionPage) => !handleShow() && !element;

  // Every action button lives inside a headless-ui Dialog whose focus is on the
  // search input. A real-browser `mousedown` on a button defaults to shifting
  // focus (blurring the input); that focus shift dismisses the picker before the
  // `click` reaches the button's onClick, so the action silently no-ops and the
  // palette closes. Cancelling the mousedown default keeps focus on the input so
  // the click always lands. (fireEvent.click in unit tests never fires mousedown,
  // which is why 417 tests missed this — see Search.test.tsx.)
  const keepPickerFocus = (event: React.MouseEvent) => event.preventDefault();

  // Single gate for every generate/refresh entry point (row click, Enter,
  // Refresh-or-Copy). The claim is taken synchronously so a second click that
  // arrives before the next render is dropped (E8).
  const runExclusive = (work: () => Promise<void>) => {
    if (generateInFlight.current) {
      return;
    }
    generateInFlight.current = true;
    void work().finally(() => {
      generateInFlight.current = false;
    });
  };

  // A generation failure is reported on the canvas: by then the palette has
  // usually closed behind the placeholder, so its inline error line would never
  // be seen. `setError` still covers the flows that keep the palette open.
  const reportFailure = (
    api: NonNullable<ReturnType<typeof getMindmapApi>>,
    message: string,
  ) => {
    setError(message);
    api.setToast?.({ message });
  };

  // Generate a mindmap from a picked result (Fix 6: the default row action).
  // Already mapped? Offer Refresh-or-Copy instead of silently duplicating —
  // database maps carry their databaseId as rootPageId, so this covers both.
  const startGenerateFlow = async (page: NotionPage) => {
    const api = getMindmapApi();
    if (!api || !page.id) {
      setError(t("mindmap.failed"));
      return;
    }
    const existing = existingMapIdsForPage(
      api.getSceneElements?.() ?? [],
      page.id,
    );
    if (existing.length > 0) {
      setError(null);
      setDuplicatePrompt({ pageId: page.id, mapIds: existing });
      return;
    }
    await startGenerate(api, page);
  };

  // "Add as card" secondary action (Fix 5c): drop a standalone mindmapNode page
  // pill (no map identity, no chip, side peek on click) — the modern replacement
  // for the old single-page "notion" card import. Legacy notion cards untouched.
  const handleAddAsCard = (page: NotionPage) => {
    const api = getMindmapApi();
    if (!api || !page.id) {
      setError(t("mindmap.failed"));
      return;
    }
    addStandaloneNotionCard(api, {
      pageId: page.id,
      title: page.title || "Untitled",
      icon: page.icon,
      isDatabase: page.type === "database",
    });
    setError(null);
    closeModal();
  };

  // Dispatch to the page or database orchestrator by result type.
  const startGenerate = async (
    api: NonNullable<ReturnType<typeof getMindmapApi>>,
    page: NotionPage,
  ) => {
    if (page.type === "database") {
      await runGenerateDb(api, page);
    } else if (page.id) {
      await runGenerate(api, page.id, page.title || "Untitled", page.icon);
    }
  };

  // A database is no longer a decision point: it generates flat, so the flow is
  // the page flow — placeholder on canvas, palette out of the way immediately.
  // Grouping is chosen afterwards, from the map root's Group by control.
  const runGenerateDb = async (
    api: NonNullable<ReturnType<typeof getMindmapApi>>,
    page: NotionPage,
  ) => {
    if (!page.id) {
      return;
    }
    setError(null);
    setDuplicatePrompt(null);
    setGeneratingId(page.id);
    const placeholderId = addGenerationPlaceholder(
      api,
      page.title || "Untitled",
    );
    closeModal();
    try {
      await generateMindmapFromDatabase(
        api,
        page.id,
        page.title || "Untitled",
        undefined,
        placeholderId,
        // The picked row already carries both, so the database's own pill can
        // stand in for the fetch instead of a grey "Building…" label.
        { title: page.title || "Untitled", icon: page.icon },
      );
      setGeneratingId(null);
    } catch (err) {
      setGeneratingId(null);
      removeGenerationPlaceholder(api, placeholderId);
      reportFailure(
        api,
        err instanceof EmptyDatabaseMindmapError
          ? t("mindmap.emptyDatabase")
          : t("mindmap.failed"),
      );
    }
  };

  const runGenerate = async (
    api: NonNullable<ReturnType<typeof getMindmapApi>>,
    pageId: string,
    title: string,
    icon?: NotionPage["icon"],
  ) => {
    setError(null);
    setDuplicatePrompt(null);
    setGeneratingId(pageId);
    // Arm the placeholder first, then close: the user gets the palette out of
    // the way immediately and watches the map build in the spot it will occupy,
    // instead of staring at a grey "Generating…" label for the whole fetch. The
    // placeholder is uncommitted, and the map commit that replaces it is the
    // flow's only history entry.
    const placeholderId = addGenerationPlaceholder(api, title);
    closeModal();
    try {
      // The title and icon are already on the picked row, so the map's real
      // root pill can be drawn immediately instead of after the fetch.
      await generateMindmapFromPage(api, pageId, placeholderId, {
        title,
        icon,
      });
      setGeneratingId(null);
    } catch (err) {
      setGeneratingId(null);
      removeGenerationPlaceholder(api, placeholderId);
      reportFailure(
        api,
        err instanceof EmptyMindmapError
          ? t("mindmap.emptyPage")
          : t("mindmap.failed"),
      );
    }
  };

  const runRefresh = async (
    api: NonNullable<ReturnType<typeof getMindmapApi>>,
    mapIds: string[],
    pageId: string,
  ) => {
    setError(null);
    setDuplicatePrompt(null);
    setGeneratingId(pageId);
    // The maps are already on canvas, so there is nothing to hold a spot for:
    // close straight away and let refreshMapsForPage report via its own toasts.
    closeModal();
    try {
      await refreshMapsForPage(api, mapIds, pageId);
      setGeneratingId(null);
    } catch {
      setGeneratingId(null);
      reportFailure(api, t("mindmap.failed"));
    }
  };

  const handlePageImport = (selectedPage: NotionPage) => {
    if (
      mouseClickEvent?.pointerDownCoordinates?.length !== 2 ||
      !selectedPage.id
    ) {
      return;
    }
    // C9#4: pages and databases alike link as one live pill — the old "Import
    // Entire database" card scatter is retired, databases become a mindmap via
    // the Generate action instead. It goes through `render` rather than
    // addStandaloneNotionCard so the element-link case can hand the editor the
    // shape it replaces: placement is then the shape's, not the viewport's.
    render({
      type: "mindmapNode",
      mindmapNode: standaloneNotionCardSpec({
        pageId: selectedPage.id,
        title: selectedPage.title || "Untitled",
        icon: selectedPage.icon,
        isDatabase: selectedPage.type === "database",
      }),
      pointerDownCoords: mouseClickEvent.pointerDownCoordinates,
      replacesElementId:
        element && eventType === "linkNotionPageForExisting"
          ? element.element.id
          : undefined,
    });
    setError(null);
    closeModal();
  };

  const runNotionSearch = async (searchQuery: string, requestId: number) => {
    const { data: searchData, error: searchError } = await notionSearch({
      query: searchQuery,
    });
    if (requestId !== searchSeq.current) {
      return;
    }
    if (searchQuery !== queryRef.current.trim()) {
      // Dropped, but nothing newer is coming — a row clicked before the debounce
      // landed left the spinner up for good, hiding "No results found".
      setIsSearchLoading(false);
      return;
    }
    if (searchError) {
      handleSupabaseError(searchError, setError, setIsSearchLoading);
    } else if (searchData) {
      setRemoteSearchResults(searchData.map(mapNotionPageResult));
    }
    setIsSearchLoading(false);
  };

  const handleNotionSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value;
    setQuery(searchQuery);
    queryRef.current = searchQuery;
    setSearchElementIndex(0);
    // A failed fallback search used to leave its error line up for good — and
    // the error branch renders BEFORE the failed-load branch, so it could also
    // hide the retry button that is the only way back.
    setError(null);
    setSelectedPage &&
      setSelectedPage((prev) => ({
        ...prev,
        id: "",
        title: "",
        type: "",
      }));

    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchSeq.current += 1;

    if (!searchQuery.trim()) {
      setRemoteSearchResults([]);
      setIsSearchLoading(false);
      return;
    }

    setRemoteSearchResults([]);
    if (listIsComplete) {
      // Everything the user can reach is already in memory, so the filter above
      // IS the search. This is the whole point of the workspace list: no request
      // per keystroke.
      setIsSearchLoading(false);
      return;
    }

    const requestId = searchSeq.current;
    const queryAtRequest = searchQuery.trim();
    setIsSearchLoading(true);
    searchTimer.current = setTimeout(() => {
      void runNotionSearch(queryAtRequest, requestId);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSelectPage = (page: NotionPage) => {
    // E8: the row's own onClick stayed live for the whole fetch (only the
    // trailing buttons were disabled), so a double click produced two complete
    // maps and two undo entries. `page` is also undefined when Enter fires
    // against an empty result list, which used to blow up unobserved inside an
    // un-awaited async.
    if (!page || generateInFlight.current) {
      return;
    }
    // Fix 6: in the plain import flow, clicking a result row now GENERATES the
    // mindmap by default (with the Refresh-or-Copy prompt for an already-mapped
    // source). The old link-a-card import is the "Add as card" secondary action.
    if (canGenerateMindmap(page)) {
      runExclusive(() => startGenerateFlow(page));
      return;
    }
    // Element-link / add-page flows keep their existing row-click behavior.
    if (
      eventType === "addNotionPage" ||
      renderPage ||
      eventType === "createNotionPageForExisting"
    ) {
      setSelectedPage && setSelectedPage(page);
      setQuery(page?.title as string);
      // The ref exists to mirror `query`; letting it drift here left the
      // fallback search comparing its response against a stale value.
      queryRef.current = page?.title ?? "";
    } else if (eventType === "importFromNotion" || !renderPage) {
      handlePageImport(page);
    }
  };

  const scrollInto = (viewIndex: number) => {
    const selectedElement = document.getElementById(`page-${viewIndex}`);
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleKeyboard = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Escape is handled before the hover guard below: it has to work even while
    // the pointer sits over a row, and inside a Refresh-or-Copy prompt it backs
    // out of the decision instead of throwing away the whole palette and the
    // user's query (C8#9).
    if (event.code === "Escape") {
      if (duplicatePrompt) {
        setDuplicatePrompt(null);
        return;
      }
      closeModal();
      return;
    }
    if (!searchIndexVisible) {
      return;
    }
    // Arrowing an EMPTY list used to walk the index off the end: the list now
    // loads asynchronously under a live keyboard handler, so three presses
    // during the load left the selection on row 4 with nothing highlighted, and
    // Enter then generated from a row the user never saw.
    if (
      (event.code === "ArrowUp" || event.code === "ArrowDown") &&
      displayedPages.length === 0
    ) {
      return;
    }
    switch (event.code) {
      // The NEXT index is what gets scrolled to. Passing the render's value
      // scrolled to the row before the highlighted one, every time — invisible
      // at ten results, not at five hundred.
      case "ArrowUp": {
        const from = Math.min(searchElementIndex, displayedPages.length - 1);
        const next = from <= 0 ? displayedPages.length - 1 : from - 1;
        setSearchElementIndex(next);
        scrollInto(next);
        return;
      }
      case "ArrowDown": {
        // Clamped, not just guarded: an index left past the end by a list that
        // shrank never recovered by arrowing down.
        const from = Math.min(searchElementIndex, displayedPages.length - 1);
        const next = from === displayedPages.length - 1 ? 0 : from + 1;
        setSearchElementIndex(next);
        scrollInto(next);
        return;
      }
      case "Enter":
        // No results (or the index has outrun a shrunken list) → nothing to
        // confirm. Without this, Enter handed `undefined` to the generate flow.
        if (displayedPages[searchElementIndex]) {
          handleSelectPage(displayedPages[searchElementIndex]);
        }
        break;
      default:
        break;
    }
  };

  const handleShow = () => {
    if (
      eventType === "addNotionPage" ||
      eventType === "createNotionPageForExisting"
    ) {
      return true;
    } else if (renderPage) {
      return true;
    }
    return false;
  };

  // C8#5: the same error line was written out twice, in the header slot and in
  // the no-results slot. One definition, used by both branches.
  const renderErrorLine = () => (
    <p className="text-center py-3 text-sm text-red-400">{error}</p>
  );

  const renderResult = (page: NotionPage) => {
    return (
      <div className="flex w-full min-w-0 justify-between">
        <div className="flex min-w-0 items-center gap-1">
          <div className="w-6 h-6 mx-1 flex shrink-0 items-center justify-start">
            {processNotionIcon(page.icon as NotionIconElementType)}
          </div>
          {/* Truncate, don't wrap: a long title used to push the row's actions
              off and reflow the list as prompts opened. */}
          <p className="truncate">{page.title}</p>
        </div>
      </div>
    );
  };

  // The Refresh-or-Copy decision for a picked row, or null when the row has no
  // pending decision. Rendered on a second line under the title (C9#3): inline
  // on the title's line it squeezed the label into three wrapped words and
  // crowded out the page name at the picker's width.
  const renderRowPrompt = (page: NotionPage) => {
    if (duplicatePrompt?.pageId === page.id) {
      return (
        <div
          className="flex flex-wrap items-center gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          <span className="whitespace-nowrap text-xs text-gray-400">
            {t("mindmap.alreadyMapped")}
          </span>
          <button
            type="button"
            data-testid="refresh-existing"
            disabled={generatingId === page.id}
            onMouseDown={keepPickerFocus}
            onClick={(event) => {
              event.stopPropagation();
              const api = getMindmapApi();
              const mapIds = duplicatePrompt?.mapIds;
              if (api && page.id && mapIds?.length) {
                // Every map of this page, not just the first: leaving the rest
                // stale is exactly the bug this prompt was meant to prevent.
                const pageId = page.id;
                runExclusive(() => runRefresh(api, mapIds, pageId));
              }
            }}
            className="whitespace-nowrap rounded-md bg-indigo-500 px-3 py-1 text-xs text-white hover:bg-indigo-600 disabled:opacity-60"
          >
            {t("mindmap.refreshExisting")}
          </button>
          <button
            type="button"
            data-testid="new-copy"
            disabled={generatingId === page.id}
            onMouseDown={keepPickerFocus}
            onClick={(event) => {
              event.stopPropagation();
              const api = getMindmapApi();
              if (api) {
                runExclusive(() => startGenerate(api, page));
              }
            }}
            className="whitespace-nowrap rounded-md border border-indigo-300 px-3 py-1 text-xs text-indigo-600 hover:bg-indigo-50 disabled:opacity-60"
          >
            {t("mindmap.newCopy")}
          </button>
          {/* C8#9: the prompt was a dead end — no way out but Escape, which
              closed the entire palette. Cancel drops back to the results. */}
          <button
            type="button"
            data-testid="prompt-cancel"
            disabled={generatingId === page.id}
            onMouseDown={keepPickerFocus}
            onClick={(event) => {
              event.stopPropagation();
              setDuplicatePrompt(null);
            }}
            className="whitespace-nowrap rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-60"
          >
            {t("mindmap.searchCancel")}
          </button>
        </div>
      );
    }

    return null;
  };

  // The trailing actions on a generatable (import-flow) row with no pending
  // decision.
  //
  // Two rounds of correction landed here. "Add as card" alone read as the row's
  // primary action and got clicked instead of the page (C9#2), so Generate
  // became a filled button — which turned the list into a column of loud CTAs
  // competing with the page titles the user is actually reading (V3 P8). Both
  // are quiet now and both are always visible: Generate carries a tinted
  // background and sits last (the row click does the same thing), "Add as card"
  // is plain text beside it. Emphasis by weight and order, not by fill.
  const renderRowAction = (page: NotionPage) => {
    if (generatingId === page.id) {
      return (
        <span className="whitespace-nowrap px-3 py-1 text-xs text-gray-400">
          {t("mindmap.generating")}
        </span>
      );
    }
    return (
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          data-testid="add-as-card"
          title={t("mindmap.addAsCard")}
          onMouseDown={keepPickerFocus}
          onClick={(event) => {
            event.stopPropagation();
            handleAddAsCard(page);
          }}
          className="whitespace-nowrap rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          {t("mindmap.addAsCard")}
        </button>
        {/* Mirrors the row click rather than adding a path: it exists to name
            the action, so the row stops looking like it does nothing. */}
        <button
          type="button"
          data-testid="generate-mindmap"
          onMouseDown={keepPickerFocus}
          onClick={(event) => {
            event.stopPropagation();
            handleSelectPage(page);
          }}
          className="whitespace-nowrap rounded-md bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-100"
        >
          {t("mindmap.generate")}
        </button>
      </div>
    );
  };

  // Title + trailing actions, or — while this row holds a pending decision —
  // the prompt on its own line beneath the title, indented past the icon.
  const renderGeneratableRow = (page: NotionPage) => {
    const prompt = renderRowPrompt(page);
    if (prompt) {
      return (
        <div className="w-full">
          {renderResult(page)}
          <div className="mt-2 pl-8">{prompt}</div>
        </div>
      );
    }
    return (
      <div className="flex w-full items-center justify-between gap-2">
        {renderResult(page)}
        {renderRowAction(page)}
      </div>
    );
  };

  return (
    <>
      <div>
        <input
          className="border-[1.5px] border-gray-400 rounded-md w-full py-3 px-4 text-md focus-visible:border-[1.5px] focus-visible:border-indigo-400 focus-visible:outline-0"
          type="text"
          placeholder="Search notion pages"
          onChange={handleNotionSearch}
          onKeyDown={handleKeyboard}
          value={query}
        />
        {displayedPages.length ? (
          <div className="flex mt-10 justify-center gap-10">
            <div className="flex items-center gap-2 rounded-md">
              <ArrowsUpDownIcon className="h-6 p-1 bg-indigo-100  w-6 rounded-md text-gray-900" />
              <span className="text-xs text-gray-400">Select</span>
            </div>
            <div className="flex items-center gap-2 rounded-md">
              <ArrowUturnLeftIcon className="h-6 p-1 bg-indigo-100  w-6 rounded-md text-gray-900" />
              <span className="text-xs text-gray-400">Confirm</span>
            </div>
            <div className=" flex items-center gap-2 rounded-md">
              <p className="p-1 text-xs px-2 rounded-md text-gray-900 bg-indigo-100">
                Esc
              </p>
              <span className="text-xs text-gray-400">Close</span>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      {error && !isLoading && renderErrorLine()}
      <div className="mt-5">
        <>
          {!isLoading && displayedPages.length && !selectedPage?.id ? (
            <>
              <h4 className="text-lg pb-4 text-gray-800">
                {queryTrimmed
                  ? t("mindmap.pickerSuggestions")
                  : t("mindmap.pickerAvailablePages")}
              </h4>
              <div className="overflow-y-scroll z-50 h-auto max-h-[40vh] ">
                {displayedPages.map((page, index) => (
                  <div
                    // Guarded: an unconditional write here re-rendered all 500
                    // rows on every row-to-row pointer move.
                    onMouseEnter={() =>
                      searchIndexVisible && setSearchIndexVisible(false)
                    }
                    onMouseLeave={() => {
                      if (!searchIndexVisible) {
                        setSearchIndexVisible(true);
                      }
                      setSearchElementIndex(index);
                    }}
                    className={classNames(
                      searchIndexVisible && searchElementIndex === index
                        ? " bg-gray-100"
                        : "",
                      !element
                        ? "hover:bg-gray-100  py-3 px-2"
                        : (element && page.type !== "database") ||
                          eventType === "createNotionPageForExisting"
                        ? " py-3 px-2 hover:bg-gray-100"
                        : "",
                      " w-full flex justify-between items-center gap-2 hover:cursor-pointer rounded-md text-gray-600 text-sm",
                    )}
                    key={index}
                    id={`page-${index}`}
                    data-testid={`result-row-${page.id}`}
                    onMouseDown={
                      canGenerateMindmap(page) ? keepPickerFocus : undefined
                    }
                    onClick={() => handleSelectPage(page)}
                  >
                    {canGenerateMindmap(page)
                      ? renderGeneratableRow(page)
                      : page.type === "database"
                      ? (!element ||
                          eventType === "createNotionPageForExisting") && (
                          <div className="flex w-full justify-between">
                            {renderResult(page)}
                            <div className="text-gray-500 w-full text-end rounded-md">
                              {page.type}
                            </div>
                          </div>
                        )
                      : renderResult(page)}
                  </div>
                ))}
              </div>
            </>
          ) : error && !isLoading ? (
            renderErrorLine()
          ) : workspaceIsEmpty && !isQueryActive && !selectedPage?.id ? (
            <p
              data-testid="no-pages-shared"
              className="text-center text-gray-500 py-3 text-sm"
            >
              {t("mindmap.pickerNoPagesShared")}
            </p>
          ) : loadFailed && !selectedPage?.id ? (
            // A failed load used to leave the spinner up for the rest of the
            // session, with no way to ask again.
            <p className="text-center text-gray-500 py-3 text-sm">
              {t("mindmap.pickerLoadFailed")}{" "}
              <button
                data-testid="retry-page-list"
                type="button"
                className="underline"
                onMouseDown={keepPickerFocus}
                onClick={() => void prefetchNotionPages()}
              >
                {t("mindmap.pickerRetry")}
              </button>
            </p>
          ) : !isLoading &&
            queryTrimmed &&
            !selectedPage?.id &&
            !isSearchLoading &&
            !displayedPages.length ? (
            <p className="text-center text-gray-500 py-3 text-sm">
              {t("mindmap.pickerNoResults")}
            </p>
          ) : (
            ""
          )}
          {(isLoading || showRemoteSearchSpinner) && (
            <div className="py-3 px-5  mt-10 flex items-center justify-center w-full gap-2 text-sm rounded-md text-gray-700 my-2">
              <svg
                aria-hidden="true"
                className="inline w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-indigo-500"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              Loading...
            </div>
          )}
        </>
      </div>
    </>
  );
};

export default Search;
